import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Loader2, Sparkles, ExternalLink, AlertTriangle, Search, ArrowLeft, Image as ImageIcon } from "lucide-react";

/**
 * Iter59A — Two-step extraction UX.
 *
 * Step 1 (discovery): operator enters ONE root URL (e.g. /menu/). We
 *   call POST /api/extraction/discover → category list. UI shows
 *   checkboxes + Select all + Import selected. Nav links (about,
 *   contact, blog, pickup, delivery, ...) are never returned because
 *   the engine filters them server-side.
 *
 * Step 2 (extraction): operator picks N categories → call
 *   POST /api/extraction/run with the URLs. Backend returns the
 *   10-column preview rows. Editable grid + CSV download + Commit.
 *
 * The two steps share state so a single panel handles both screens
 * via the `step` enum: "input" | "categories" | "preview".
 */
export default function ExtractFromUrlPanelV2({ catalog, isDraft, onCommitted }) {
  const [step, setStep] = useState("input");  // "input" | "categories" | "preview"
  const [rootUrl, setRootUrl] = useState("");
  const [profile, setProfile] = useState("restaurant_menu");
  const [llmAssist, setLlmAssist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Step 1 state
  const [categories, setCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [discoveryWarnings, setDiscoveryWarnings] = useState([]);

  // Step 2 state
  const [extraction, setExtraction] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [imageModal, setImageModal] = useState(null);

  const isValidUrl = /^https?:\/\//i.test(rootUrl.trim());

  // ---- Step 1: Discover -----------------------------------------------------
  const onDiscover = async () => {
    if (!isValidUrl) {
      setError("Please enter a valid http(s) URL.");
      return;
    }
    setBusy(true); setError("");
    setCategories([]); setSelectedCats(new Set());
    try {
      const r = await api.post("/extraction/discover", {
        url: rootUrl.trim(),
        profile,
      });
      const cats = r.data.categories || [];
      setCategories(cats);
      setDiscoveryWarnings(r.data.warnings || []);
      setSelectedCats(new Set(cats.map((c) => c.url)));
      if (cats.length === 0) {
        // No categories detected — fall back to treating the URL as
        // a single extraction target (existing Iter59 behaviour).
        toast.info("No categories detected. Running extraction directly on this URL.");
        await runExtractionWithUrls([rootUrl.trim()]);
        return;
      }
      setStep("categories");
      toast.success(`Found ${cats.length} ${profile === "restaurant_menu" ? "menu" : ""} categor${cats.length === 1 ? "y" : "ies"}.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.hint || "Discovery failed."));
    } finally {
      setBusy(false);
    }
  };

  // ---- Step 2: Run extraction with chosen URLs ----------------------------
  const runExtractionWithUrls = async (urls) => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/extraction/run", {
        urls,
        profile,
        llm_assist: llmAssist,
      });
      setExtraction(r.data);
      const fresh = (r.data.rows || []).map((row, i) => ({ ...row, _idx: i }));
      setRows(fresh);
      setSelectedRows(new Set(fresh.filter((r) => !r.needs_review).map((r) => r._idx)));
      setStep("preview");
      if (r.data.was_capped) {
        toast.warning("Import reached the safe limit. You can run another import for remaining categories.");
      } else {
        toast.success(`Extracted ${fresh.length} item${fresh.length === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.hint || "Extraction failed."));
    } finally {
      setBusy(false);
    }
  };

  const onImportSelected = () => {
    const urls = Array.from(selectedCats);
    if (urls.length === 0) {
      setError("Select at least one category to import.");
      return;
    }
    runExtractionWithUrls(urls);
  };

  // ---- Preview grid helpers ------------------------------------------------
  const updateRow = (idx, key, value) =>
    setRows((prev) => prev.map((r) => (r._idx === idx ? { ...r, [key]: value } : r)));
  const toggleSelectedRow = (idx) =>
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  const toggleAllRows = () =>
    setSelectedRows(selectedRows.size === rows.length ? new Set() : new Set(rows.map((r) => r._idx)));

  const downloadCsv = async () => {
    if (!extraction?.extraction_id) return;
    try {
      const r = await api.get(`/extraction/results/${extraction.extraction_id}/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${extraction.extraction_id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("CSV download failed"); }
  };

  const commit = async () => {
    if (!extraction?.extraction_id || selectedRows.size === 0) return;
    if (!isDraft) { toast.error("Catalog must be in draft to commit."); return; }
    setBusy(true); setError("");
    try {
      const chosen = rows.filter((r) => selectedRows.has(r._idx))
        .map(({ _idx, notes, ...rest }) => rest);
      const r = await api.post(`/extraction/results/${extraction.extraction_id}/commit`,
        { catalog_id: catalog.id, rows: chosen });
      toast.success(`Committed ${r.data.inserted} item(s) to catalog`);
      // Reset to step 1
      setExtraction(null); setRows([]); setSelectedRows(new Set());
      setCategories([]); setSelectedCats(new Set()); setRootUrl("");
      setStep("input");
      await onCommitted?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.hint || "Commit failed."));
    } finally { setBusy(false); }
  };

  // ---- Render --------------------------------------------------------------

  return (
    <div className="space-y-4" data-testid="extract-url-v2-panel">
      {error && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 inline-flex items-center gap-2"
             data-testid="extract-url-v2-error">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}

      {/* STEP 1 — URL input */}
      {step === "input" && (
        <div className="space-y-3" data-testid="extract-url-v2-step-input">
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">Website URL</label>
            <input
              type="url"
              value={rootUrl}
              onChange={(e) => setRootUrl(e.target.value)}
              placeholder="https://your-restaurant.com/menu/"
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 font-mono"
              data-testid="extract-url-v2-input"
            />
            <p className="text-xs text-slate-500">
              Paste the menu landing page. We&apos;ll detect every category
              (Appetizers, Beef, Desserts, …) and let you pick which to import.
              <strong className="text-slate-700"> Navigation pages
              (About, Contact, Pickup, Delivery, Blog) are skipped automatically.</strong>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-700">Profile</label>
              <select value={profile} onChange={(e) => setProfile(e.target.value)}
                      className="text-sm border border-slate-200 rounded-md px-2 py-1"
                      data-testid="extract-url-v2-profile">
                <option value="restaurant_menu">Restaurant Menu</option>
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600" title="Opt-in.">
              <input type="checkbox" checked={llmAssist} onChange={(e) => setLlmAssist(e.target.checked)}
                     data-testid="extract-url-v2-llm" />
              <Sparkles className="w-3.5 h-3.5 text-amber-500" /> AI-assisted (opt-in)
            </label>
            <button type="button" onClick={onDiscover} disabled={busy || !isValidUrl}
                    className="ml-auto bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="extract-url-v2-discover">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {busy ? "Detecting…" : "Detect categories"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Category picker */}
      {step === "categories" && (
        <div className="space-y-3" data-testid="extract-url-v2-step-categories">
          <div className="flex items-center justify-between">
            <button onClick={() => setStep("input")} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-700">
              <ArrowLeft className="w-3.5 h-3.5" /> Change URL
            </button>
            <div className="text-xs text-slate-600">
              Found <strong className="text-slate-800">{categories.length}</strong> categor{categories.length === 1 ? "y" : "ies"} from <span className="font-mono">{rootUrl}</span>
            </div>
          </div>
          <div className="border border-slate-200 rounded-md">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs">
              <label className="inline-flex items-center gap-2 font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={selectedCats.size === categories.length && categories.length > 0}
                  onChange={() => setSelectedCats(
                    selectedCats.size === categories.length ? new Set() : new Set(categories.map((c) => c.url))
                  )}
                  data-testid="extract-url-v2-select-all-cats"
                />
                Select all
              </label>
              <span className="text-slate-500">{selectedCats.size} selected</span>
            </div>
            <ul className="divide-y divide-slate-100" data-testid="extract-url-v2-cat-list">
              {categories.map((c) => (
                <li key={c.url} className="px-3 py-2 flex items-start gap-3" data-testid={`extract-url-v2-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <input
                    type="checkbox"
                    checked={selectedCats.has(c.url)}
                    onChange={() => {
                      setSelectedCats((prev) => {
                        const next = new Set(prev);
                        if (next.has(c.url)) next.delete(c.url); else next.add(c.url);
                        return next;
                      });
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{c.name}</div>
                    <a href={c.url} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1 hover:underline">
                      <ExternalLink className="w-3 h-3" /> {c.url}
                    </a>
                  </div>
                  <div className="text-[11px] text-emerald-700 font-medium">{Math.round(c.confidence * 100)}%</div>
                </li>
              ))}
            </ul>
          </div>
          {discoveryWarnings.length > 0 && (
            <ul className="text-xs text-amber-700 list-disc pl-5">{discoveryWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onImportSelected} disabled={busy || selectedCats.size === 0}
                    className="bg-emerald-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="extract-url-v2-import-selected">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Import {selectedCats.size} categor{selectedCats.size === 1 ? "y" : "ies"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Preview grid */}
      {step === "preview" && extraction && (
        <div className="space-y-2" data-testid="extract-url-v2-preview">
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(categories.length > 0 ? "categories" : "input")} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-700">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <div className="text-xs text-slate-600">
              {rows.length} extracted · {rows.filter((r) => r.needs_review).length} need review · avg {Math.round((extraction.avg_confidence || 0) * 100)}%
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={downloadCsv}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50"
                      data-testid="extract-url-v2-download-csv">
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
              <button type="button" onClick={commit} disabled={busy || selectedRows.size === 0 || !isDraft}
                      title={!isDraft ? "Catalog must be in draft to commit." : undefined}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-emerald-600 text-white rounded-md disabled:opacity-50"
                      data-testid="extract-url-v2-commit">
                Commit {selectedRows.size} to catalog
              </button>
            </div>
          </div>
          {extraction.was_capped && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="extract-url-v2-capped">
              Import reached the safe limit ({rows.length} items). Run another import for remaining categories.
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded-md">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-2 w-8">
                    <input type="checkbox" checked={selectedRows.size === rows.length && rows.length > 0}
                           onChange={toggleAllRows} data-testid="extract-url-v2-select-all" />
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Image</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Category</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Code</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Product Name</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Description</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Price</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Item Page</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Conf.</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-700">Review</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._idx} className={`border-t border-slate-100 ${r.needs_review ? "bg-amber-50/40" : ""}`}
                      data-testid={`extract-url-v2-row-${r._idx}`}>
                    <td className="px-2 py-1 align-top">
                      <input type="checkbox" checked={selectedRows.has(r._idx)}
                             onChange={() => toggleSelectedRow(r._idx)}
                             data-testid={`extract-url-v2-select-${r._idx}`} />
                    </td>
                    <td className="px-2 py-1 align-top">
                      {r.image_url ? (
                        <button type="button" onClick={() => setImageModal(r.image_url)}
                                className="block w-10 h-10 rounded overflow-hidden border border-slate-200 hover:ring-2 hover:ring-emerald-300"
                                data-testid={`extract-url-v2-thumb-${r._idx}`}>
                          <img src={r.image_url} alt={r.product_name} className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ) : (
                        <ImageIcon className="w-4 h-4 text-slate-300" />
                      )}
                    </td>
                    {["category", "item_code", "product_name", "description", "price"].map((k) => (
                      <td key={k} className="px-2 py-1 align-top">
                        <input
                          type={k === "price" ? "number" : "text"}
                          step={k === "price" ? "0.01" : undefined}
                          value={r[k] == null ? "" : r[k]}
                          onChange={(e) => updateRow(r._idx, k,
                            k === "price"
                              ? (e.target.value === "" ? null : Number(e.target.value))
                              : e.target.value)}
                          className="w-full border border-transparent focus:border-slate-300 rounded px-1.5 py-0.5 bg-transparent"
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1 align-top">
                      {r.item_page_url ? (
                        <a href={r.item_page_url} target="_blank" rel="noreferrer"
                           className="text-slate-600 hover:underline inline-flex items-center gap-1" title={r.item_page_url}>
                          <ExternalLink className="w-3 h-3" /> open
                        </a>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-1 align-top">
                      <span className={`text-xs font-medium ${r.extraction_confidence >= 0.8 ? "text-emerald-700" : r.extraction_confidence >= 0.6 ? "text-amber-700" : "text-rose-700"}`}>
                        {Math.round((r.extraction_confidence || 0) * 100)}%
                      </span>
                    </td>
                    <td className="px-2 py-1 align-top">
                      <input type="checkbox" checked={!!r.needs_review}
                             onChange={(e) => updateRow(r._idx, "needs_review", e.target.checked)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Full-image modal — Iter59A storefront-style preview */}
      {imageModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setImageModal(null)} data-testid="extract-url-v2-image-modal">
          <img src={imageModal} alt="" className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
